import {
  AccountStatus,
  BaseUserProfile,
  EnabledFeatures,
  GymEnvironment,
  homePlanEnvironmentMapForFreeAppAccess,
  homePlanEnvironmentMapForInitialUserSignup,
  IAccountChangeStatus,
  IChangeResult,
  IStripeCustomerSubscriptionDataWrapper,
  ISubscriptionCreateArguments,
  productEnvironmentMapForElitePro,
  productEnvironmentMapForEliteStarter,
  productEnvironmentMapForIAP,
  productEnvironmentMapForLite,
  studioPlanEnvironmentMapForInitialUserSignup,
} from 'fitbeat.models';
import { IMappedPlan, MemberContractDetails, PaymentService } from 'fitbeat.payment-service';
import Stripe from 'stripe';
import { Winston } from 'winston';
import { MarketingAnalyticsService } from '../marketing-analytics';
import { UserProfileService } from '../user';
import { EliteSubscriptionToFamilyPlan } from './EliteSubscriptionToFamilyPlan';
import { FailWithMessage } from './FailWithMessage';
import { FamilyPlanSubscriptionToFamilyPlan } from './FamilyPlanSubscriptionToFamilyPlan';
import { FreeAppAccessToFamilySubscription } from './FreeAppAccessToFamilySubscription';
import { ImmediateCancelCurrentAndSubscribe } from './ImmediateCancelCurrentAndSubscribe';
import { InAppPurchaseSubscriptionToFamilyPlan } from './InAppPurchaseSubscriptionToFamilyPlan';
import { InAppPurchaseToLiteSubscription } from './InAppPurchaseToLiteSubscription';
import { InHomeTrialSubscriptionToFamilyPlan } from './InHomeTrialSubscriptionToFamilyPlan';
import { LiteSubscriptionToFamilyPlan } from './LiteSubscriptionToFamilyPlan';
import { NoActiveSubscriptionToFamilyPlan } from './NoSubscriptionToFamilyPlan';
import { NoSubscriptionToLiteSubscription } from './NoSubscriptionToLiteSubscription';
import { NoSubscriptionToStudioSubscription } from './NoSubscriptionToStudioSubscription';
import { NoSubscriptionToSubscription } from './NoSubscriptionToSubscription';
import { StudioSubscriptionToFamilyPlan } from './StudioSubscriptionToFamilyPlan';
import { StudioSubscriptionToStudioSubscription } from './StudioSubscriptionToStudioSubscription';

interface IAccountChangeConfig {
  fromStatus: AccountStatus;
  toStatus: AccountStatus;
  change: IChangeFunction;
  trackingEventName?: string;
}

export interface IChangeFunction {
  validate(): Promise<IAccountChangeStatus>;
  change(
    userProfile: BaseUserProfile,
    currentPlan: IMappedPlan,
    paymentService: PaymentService,
    userProfileService: UserProfileService,
    familyPlanOwnerUserProfileId?: string,
    subscriptionCreateOptions?: ISubscriptionCreateArguments,
  ): Promise<IChangeResult>;
}

const accountChangeMap: IAccountChangeConfig[] = [
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'FamilySubscription',
    change: new NoActiveSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'InHomeTrial',
    toStatus: 'FamilySubscription',
    change: new InHomeTrialSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'LiteSubscription',
    toStatus: 'FamilySubscription',
    change: new LiteSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'InAppPurchase',
    toStatus: 'FamilySubscription',
    change: new InAppPurchaseSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'EliteSubscription',
    toStatus: 'FamilySubscription',
    change: new EliteSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'FamilySubscription',
    toStatus: 'EliteSubscription',
    change: new FailWithMessage(
      'FamilySubscription',
      'EliteSubscription',
      'Only the main member of a family plan may create new subscriptions.',
    ),
  },
  {
    fromStatus: 'StudioStandardSubscription',
    toStatus: 'FamilySubscription',
    change: new StudioSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'StudioContract',
    toStatus: 'FamilySubscription',
    change: new StudioSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'FamilySubscription',
    toStatus: 'FamilySubscription',
    change: new FamilyPlanSubscriptionToFamilyPlan(),
  },
  {
    fromStatus: 'InHomeTrial',
    toStatus: 'LiteSubscription',
    change: new ImmediateCancelCurrentAndSubscribe('InHomeTrial', 'LiteSubscription', 'subscription_to_lite'),
  },
  {
    fromStatus: 'LiteSubscription',
    toStatus: 'FreeAppAccess',
    change: new ImmediateCancelCurrentAndSubscribe(
      'LiteSubscription',
      'FreeAppAccess',
      'subscription_to_freelitepreelite',
    ),
  },
  {
    fromStatus: 'InHomeTrial',
    toStatus: 'FreeAppAccess',
    change: new ImmediateCancelCurrentAndSubscribe('InHomeTrial', 'FreeAppAccess', 'subscription_to_freelitepreelite'),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'FreeAppAccess',
    change: new NoSubscriptionToSubscription('FreeAppAccess', 'subscription_to_freelitepreelite'),
  },
  {
    fromStatus: 'FreeAppAccess',
    toStatus: 'EliteSubscription',
    change: new ImmediateCancelCurrentAndSubscribe('FreeAppAccess', 'EliteSubscription', 'subscription_to_elite'),
  },
  {
    fromStatus: 'InHomeTrial',
    toStatus: 'EliteSubscription',
    change: new ImmediateCancelCurrentAndSubscribe('InHomeTrial', 'EliteSubscription', 'subscription_to_elite'),
  },
  {
    fromStatus: 'FreeAppAccess',
    toStatus: 'FamilySubscription',
    change: new FreeAppAccessToFamilySubscription(),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'LiteSubscription',
    change: new NoSubscriptionToLiteSubscription(),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'InAppPurchase',
    change: new NoSubscriptionToSubscription('InAppPurchase', 'subscription_to_lite'),
  },
  {
    fromStatus: 'LiteSubscription',
    toStatus: 'EliteSubscription',
    change: new ImmediateCancelCurrentAndSubscribe('LiteSubscription', 'EliteSubscription', 'subscription_to_elite'),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'EliteSubscription',
    change: new NoSubscriptionToSubscription('EliteSubscription', 'subscription_to_elite'),
  },
  {
    fromStatus: 'InAppPurchase',
    toStatus: 'LiteSubscription',
    change: new InAppPurchaseToLiteSubscription(),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'StudioStandardSubscription',
    change: new NoSubscriptionToStudioSubscription(
      'NoActiveSubscription',
      'StudioStandardSubscription',
      'subscription_to_studio',
    ),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'StudioContract',
    change: new NoSubscriptionToStudioSubscription(
      'NoActiveSubscription',
      'StudioContract',
      'subscription_to_studio',
    ),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'InHomeTrial',
    change: new NoSubscriptionToSubscription('InHomeTrial', 'subscription_to_trial'),
  },
  {
    fromStatus: 'NoActiveSubscription',
    toStatus: 'InStudioTrial',
    change: new NoSubscriptionToStudioSubscription(
      'NoActiveSubscription',
      'InStudioTrial',
      'subscription_to_studio_trial',
    ),
  },
  {
    fromStatus: 'InStudioTrial',
    toStatus: 'StudioContract',
    change: new StudioSubscriptionToStudioSubscription(
      'InStudioTrial',
      'StudioContract',
      'subscription_to_studio',
    ),
  },
  {
    fromStatus: 'InStudioTrial',
    toStatus: 'StudioStandardSubscription',
    change: new ImmediateCancelCurrentAndSubscribe(
      'InStudioTrial',
      'StudioStandardSubscription',
      'subscription_to_studio',
    ),
  },
  {
    fromStatus: 'StudioStandardSubscription',
    toStatus: 'StudioContract',
    change: new StudioSubscriptionToStudioSubscription(
      'StudioStandardSubscription',
      'StudioContract',
      'subscription_to_studio',
    ),
  },
];

export class AccountStatusManager {
  public static isAccountStatusFree(accountStatus: AccountStatus | void): boolean {
    return accountStatus === 'InHomeTrial' || accountStatus === 'FreeAppAccess';
  }

  constructor(
    private readonly paymentService: PaymentService,
    private readonly userProfileService: UserProfileService,
    private readonly marketingAnalyticsService: MarketingAnalyticsService,
    private logger: Winston,
    //  todo: add gymEnvironment to constructor instead of passing in for each method
  ) {
  }

  public async getUserCurrentAccountStatus(
    userProfile: BaseUserProfile,
    gymEnvironment: GymEnvironment,
  ): Promise<AccountStatus> {
    const paymentInfo = await this.paymentService.getPaymentInfo(userProfile, userProfile.currentPlanId);
    return this.getAccountStatus(userProfile, paymentInfo.currentPlan, gymEnvironment);
  }

  public async getAccountStatus(
    userProfile: BaseUserProfile,
    currentPlan: IMappedPlan,
    gymEnvironment: GymEnvironment,
  ): Promise<AccountStatus> {
    if (await this.isStudioContract(userProfile, gymEnvironment)) {
      return 'StudioContract';
    }

    const userIsMemberOnAnotherUsersPlan = !!userProfile.planOwnerUserProfileId;
    if (userIsMemberOnAnotherUsersPlan) {
      return 'FamilySubscription';
    }

    if (currentPlan && BaseUserProfile.hasActivePlan(userProfile)) {
      const currentStatus = this.getStatusFromPlanAndProductIds(
        currentPlan.id,
        currentPlan.productId as string,
        gymEnvironment,
      );

      return currentStatus;
    }

    return 'NoActiveSubscription';
  }

  public async changeAccountStatus(
    userProfile: BaseUserProfile,
    expectedStatus: AccountStatus,
    gymEnvironment: GymEnvironment,
    familyPlanOwnerUserProfileId?: string,
    subscriptionCreateOptions?: ISubscriptionCreateArguments,
  ): Promise<IAccountChangeStatus> {
    const currentPlanDetails = await this.paymentService.getPaymentInfo(userProfile, subscriptionCreateOptions?.planId);
    const currentAccountStatus = await this.getAccountStatus(
      userProfile,
      currentPlanDetails.currentPlan,
      gymEnvironment,
    );
    const changer = await this.getAccountChanger(currentAccountStatus, expectedStatus);
    if (!changer) {
      return {
        fromStatus: currentAccountStatus,
        toStatus: expectedStatus,
        success: false,
        message: `Not able to change from ${currentAccountStatus} to ${expectedStatus}`,
      };
    }

    const changeValidation = await changer.change.validate();

    if (changeValidation.success) {
      const result = await changer.change.change(
        userProfile,
        currentPlanDetails.currentPlan,
        this.paymentService,
        this.userProfileService,
        familyPlanOwnerUserProfileId,
        subscriptionCreateOptions,
      );
      changeValidation.data = result.data;
      this.trackConversionEvent(result, userProfile);
    }

    return changeValidation;
  }

  public async changeAccountStatusUsingPlanId(
    userProfile: BaseUserProfile,
    gymEnvironment: GymEnvironment,
    familyPlanOwnerUserProfileId?: string,
    subscriptionCreateOptions?: ISubscriptionCreateArguments,
  ): Promise<IAccountChangeStatus> {
    if (!subscriptionCreateOptions) {
      throw new Error('subscription create options required for changeAccountStatusToPlan');
    }

    const planToChangeTo: Stripe.Plan | null = await this.paymentService.getPlanOrNull(
      subscriptionCreateOptions.planId,
    );
    if (!planToChangeTo) {
      throw new Error(`invalid plan id ${subscriptionCreateOptions.planId}`);
    }

    const expectedStatus = this.getStatusFromPlanAndProductIds(
      planToChangeTo.id,
      planToChangeTo.product as string,
      gymEnvironment,
    );
    if (!expectedStatus) {
      throw new Error('Expected plan status not found');
    }

    return this.changeAccountStatus(
      userProfile,
      expectedStatus,
      gymEnvironment,
      familyPlanOwnerUserProfileId,
      subscriptionCreateOptions,
    );
  }

  public async validateAccountStatusChange(
    userProfile: BaseUserProfile,
    planId: string,
    gymEnvironment: GymEnvironment,
  ): Promise<IAccountChangeStatus> {
    const planToChangeTo: Stripe.Plan | null = await this.paymentService.getPlanOrNull(planId);
    if (!planToChangeTo) {
      throw new Error(`invalid plan id ${planId}`);
    }

    const expectedStatus = this.getStatusFromPlanAndProductIds(
      planToChangeTo.id,
      planToChangeTo.product as string,
      gymEnvironment,
    );
    if (!expectedStatus) {
      throw new Error('Expected plan status not found');
    }

    const currentPlanDetails = await this.paymentService.getPaymentInfo(userProfile);
    const currentAccountStatus = await this.getAccountStatus(
      userProfile,
      currentPlanDetails.currentPlan,
      gymEnvironment,
    );
    const changer = await this.getAccountChanger(currentAccountStatus, expectedStatus);
    if (!changer) {
      return {
        fromStatus: currentAccountStatus,
        toStatus: expectedStatus,
        success: false,
        message: `Not able to change from ${currentAccountStatus} to ${expectedStatus}`,
      };
    }

    return changer.change.validate();
  }

  public getStatusFromPlanAndProductIds(
    planId: string,
    productId: string,
    gymEnvironment: GymEnvironment,
  ): AccountStatus {
    // TODO: If price has accountStatus metadata return that value
    if (planId === homePlanEnvironmentMapForFreeAppAccess[gymEnvironment]) {
      return 'FreeAppAccess';
    }

    if (studioPlanEnvironmentMapForInitialUserSignup[gymEnvironment].indexOf(planId) !== -1) {
      return 'InStudioTrial';
    }

    if (homePlanEnvironmentMapForInitialUserSignup[gymEnvironment].indexOf(planId) !== -1) {
      return 'InHomeTrial';
    }

    if (productId === productEnvironmentMapForIAP[gymEnvironment]) {
      return 'InAppPurchase';
    }

    if (productEnvironmentMapForLite[gymEnvironment].includes(productId)) {
      return 'LiteSubscription';
    }

    if (
      productId === productEnvironmentMapForElitePro[gymEnvironment]
      || productId === productEnvironmentMapForEliteStarter[gymEnvironment]
    ) {
      return 'EliteSubscription';
    }

    const price = this.paymentService.getPriceInfo(planId);
    if (
      price
      && price.metadata.originalContractDurationUnit
      && price.metadata.originalContractDuration
    ) {
      return 'StudioContract';
    }

    return 'StudioStandardSubscription';
  }

  private async getAccountChanger(currentAccountStatus: AccountStatus, toStatus: AccountStatus) {
    return accountChangeMap.find((change: IAccountChangeConfig) => change.fromStatus === currentAccountStatus
        && change.toStatus === toStatus);
  }

  private trackConversionEvent(changeResult: IChangeResult, userProfile: BaseUserProfile): void {
    if (!changeResult.marketingAnalyticsData || changeResult.marketingAnalyticsData === 'no-marketing-analytics') {
      return;
    }
    this.marketingAnalyticsService.logEventInBackground(
      changeResult.marketingAnalyticsData.trackingEventName,
      userProfile,
      changeResult.marketingAnalyticsData.trackingData,
    );
  }

  private async isStudioContract(userProfile: BaseUserProfile, gymEnvironment: GymEnvironment): Promise<boolean> {
    const enabledFeatures = await this.userProfileService.getEnabledFeaturesForUser(userProfile.uid, gymEnvironment);
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    if (hasAccessToAStudioGym && userProfile.stripeUserId) {
      const activeSubscription = await this.paymentService.getUsersActiveSubscription(userProfile);
      const contractDetails = activeSubscription && MemberContractDetails
        .calculateContractDetails(this.logger, activeSubscription as IStripeCustomerSubscriptionDataWrapper);

      return !!contractDetails?.isContract;
    }

    return false;
  }
}
